"use client"

import ky, { HTTPError } from "ky"
import { useState } from "react"
import type { FormEvent } from "react"

type Receipt = {
  readonly reference: string
  readonly receivedAt: string
}

export const PartnerForm = () => {
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState<string>()
  const [successful, setSuccessful] = useState(false)
  const [fileName, setFileName] = useState("선택된 파일 없음")

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    setSubmitting(true)
    setMessage(undefined)
    setSuccessful(false)
    try {
      const receipt = await ky.post("/api/public/partner-inquiries", { body: new FormData(form), retry: 0, timeout: 10_000 }).json<Receipt>()
      form.reset()
      setFileName("선택된 파일 없음")
      setSuccessful(true)
      setMessage(`문의가 접수되었습니다. 접수 번호 ${receipt.reference}`)
    } catch (error) {
      if (error instanceof HTTPError) {
        setMessage(error.response.status === 429 ? "요청이 많습니다. 잠시 후 다시 시도해 주세요." : "입력 내용과 첨부파일을 다시 확인해 주세요.")
      } else if (error instanceof Error) {
        setMessage("문의 채널을 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.")
      } else {
        throw error
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form className="partner-form" encType="multipart/form-data" onSubmit={submit}>
      <div className="form-grid">
        <label>
          <span>회사명</span>
          <input autoComplete="organization" maxLength={100} name="company" required />
        </label>
        <label>
          <span>담당자명</span>
          <input autoComplete="name" maxLength={50} name="contactName" required />
        </label>
        <label className="form-wide">
          <span>업무 이메일</span>
          <input autoComplete="email" maxLength={200} name="email" required type="email" />
        </label>
        <label>
          <span>제휴 유형</span>
          <select defaultValue="" name="inquiryType" required>
            <option disabled value="">
              선택해 주세요
            </option>
            <option value="agency">대행사</option>
            <option value="media">매체</option>
            <option value="supplier">상품 공급사</option>
          </select>
        </label>
        <label>
          <span>예상 월간 규모</span>
          <select defaultValue="unknown" name="expectedVolume">
            <option value="unknown">아직 모름</option>
            <option value="monthly-under-10000">1만 건 미만</option>
            <option value="monthly-10000-100000">1만~10만 건</option>
            <option value="monthly-over-100000">10만 건 이상</option>
          </select>
        </label>
        <label className="form-wide">
          <span>문의 내용</span>
          <textarea maxLength={2_000} minLength={10} name="message" required rows={6} />
          <small>주민등록번호, 카드번호, 계정 비밀번호, 쿠폰 PIN은 입력하지 마세요.</small>
        </label>
        <label className="form-wide file-field">
          <span>제안 자료</span>
          <input accept=".pdf,.png,.jpg,.jpeg,.webp" className="file-input" name="attachment" onChange={(event) => setFileName(event.currentTarget.files?.[0]?.name ?? "선택된 파일 없음")} type="file" />
          <span className="file-picker">
            <strong>파일 선택</strong>
            <span aria-live="polite" className="file-name">
              {fileName}
            </span>
          </span>
          <small>PDF, PNG, JPG, WebP · 최대 5MB · 실행 파일은 차단됩니다.</small>
        </label>
        <label className="honeypot" hidden tabIndex={-1}>
          <span>웹사이트</span>
          <input autoComplete="off" name="website" tabIndex={-1} />
        </label>
      </div>
      <label className="consent-row">
        <input name="consent" required type="checkbox" value="true" />
        <span>개인정보 수집 및 이용에 동의합니다</span>
      </label>
      <p className="form-privacy">문의 답변을 위해 회사명, 담당자명, 업무 이메일과 문의 내용을 수집합니다. 제휴 검토가 끝난 뒤 보존 기준에 따라 안전하게 처리합니다.</p>
      <button aria-busy={submitting} className="action action-primary submit-action" disabled={submitting} type="submit">
        제휴 문의 보내기
      </button>
      {message === undefined ? null : <output className={successful ? "form-result form-result-success" : "form-result form-result-error"}>{message}</output>}
    </form>
  )
}
